import { Form, Select } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";

import SelectTerritorialUnitsCode from "features/territorialUnit/ui/SelectTerritorialUnitsCode";
import {
  dataSourceStatusOptions,
  divisionOptions,
} from "shared/ui/TableSelect";
import {
  DescriptionField,
  HandbookRefField,
  SelectField,
  TextField,
} from "./HandbookFormControls";
import { HandbookFormProps, makeIsRequired } from "./handbookFormProps";
import styles from "./HandbookFormModal.module.css";

type Props = HandbookFormProps & {
  form: FormInstance;
  language: "ru" | "ky";
  /** Регион записи — состоянием владеет модалка, она же сбрасывает его при открытии. */
  selectedRegion: any;
  onRegionChange: (region: any) => void;
};

/** Собственные поля записи: код, названия, признаки, описания, банк и адрес. */
export const HandbookMainFields = ({
  form,
  language,
  selectedRegion,
  onRegionChange,
  ...p
}: Props) => {
  const { t } = useTranslation();
  const isRequired = makeIsRequired(p.requiredFields);

  return (
    <>
      {(p.isAllHandbooks || p.isAllServices) && (
        <TextField
          name="code"
          label={t("handbook.table.code")}
          message="Укажите код"
          required={isRequired("code")}
          disabled={!!p.initial?.code}
        />
      )}

      <TextField
        name="nameRu"
        label={t("handbook.table.nameRu")}
        message="Укажите название (RU)"
        required={isRequired("nameRu")}
        readOnly={p.isTerritorialUnit}
      />
      <TextField
        name="nameKy"
        label={t("handbook.table.nameKy")}
        message="Укажите название (KY)"
        required={isRequired("nameKy")}
        readOnly={p.isTerritorialUnit}
      />

      {p.showDataSourceStatus && (
        <SelectField
          name="dataSourceStatus"
          label="Статус источника данных"
          message="Укажите статус источника данных"
          required={isRequired("dataSourceStatus")}
          placeholder="Выберите статус источника данных"
          options={dataSourceStatusOptions}
        />
      )}

      {p.showDivision && (
        <SelectField
          name="division"
          label="Подразделение"
          message="Укажите подразделение"
          required={isRequired("division")}
          placeholder="Выберите подразделение"
          options={divisionOptions}
        />
      )}

      {p.showAlias && (
        <TextField
          name="alias"
          label="Алиас"
          message="Укажите алиас"
          required={isRequired("alias")}
        />
      )}

      {p.showDescriptionRu && (
        <DescriptionField
          name="descriptionRu"
          label="Описание на русском"
          rich={p.isAgroCredit}
          required={isRequired("descriptionRu")}
        />
      )}

      {p.showDescriptionKy && (
        <DescriptionField
          name="descriptionKy"
          label="Описание на кыргызском"
          rich={p.isAgroCredit}
          required={isRequired("descriptionKy")}
        />
      )}

      {p.showBank && p.bankFieldKey && (
        <HandbookRefField
          name="bankId"
          label="Банк"
          message="Укажите банк"
          required={isRequired(`${p.bankFieldKey}Id`)}
          fieldKey={p.bankFieldKey}
          initial={p.initial}
          language={language}
        />
      )}

      {p.showBik && (
        <TextField
          name="bik"
          label="Банковский идентификационный код"
          message="Укажите банковский идентификационный код"
          required={isRequired("bik")}
        />
      )}

      {p.showBik && p.showRegion && (
        <Form.Item
          className={styles.formItem}
          label="Регион"
          name="regionId"
          rules={
            isRequired("regionId")
              ? [{ required: true, message: "Укажите регион" }]
              : []
          }
        >
          <SelectTerritorialUnitsCode
            value={selectedRegion}
            size="large"
            type={["REGION", "CITY_OF_REPUBLICAN_SIGNIFICANCE"]}
            onChange={(value: any) => {
              onRegionChange(value);
              form.setFieldsValue({ regionId: value?.id ?? null });
            }}
          />
        </Form.Item>
      )}

      {p.showAddress && (
        <TextField
          name="address"
          label="Адрес"
          message="Укажите адрес"
          required={isRequired("address")}
        />
      )}

      {p.isAllHandbooks && (
        <Form.Item name="serviceCodes" label="Сервис">
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Выберите сервис"
            options={p.serviceOptions ?? []}
            optionFilterProp="label"
          />
        </Form.Item>
      )}
    </>
  );
};
